<template>
    <div class="position-relative property-picker-wrapper" :data-property-picker-wrapper="pickerType">
        <!-- Native select for form submit and automation -->
        <select
            :name="name"
            :value="internalValue"
            class="d-none property-picker-native-select"
            :data-property-picker-select="pickerType"
            tabindex="-1"
            aria-hidden="true"
            @change="onNativeSelectChange($event.target.value)"
        >
            <option value="">{{ placeholder }}</option>
            <option v-for="opt in normalizedOptions" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
        </select>

        <!-- Toggle Button -->
        <button
            ref="toggleBtn"
            type="button"
            class="form-control text-left d-flex justify-content-between align-items-center property-picker-toggle"
            :class="{'is-placeholder': !internalValue}"
            :data-property-picker-toggle="pickerType"
            :disabled="disabled"
            @click="toggleDropdown"
            @keydown.down.prevent="openDropdown"
            @keydown.enter.prevent="toggleDropdown"
            @keydown.esc.prevent="closeDropdown"
        >
            <span class="text-truncate property-picker-selected-label">{{ selectedLabel }}</span>
            <span class="caret ml-2"></span>
        </button>

        <!-- Searchable Dropdown Menu -->
        <div
            v-if="isOpen"
            class="dropdown-menu show property-picker-dropdown shadow-sm"
            :data-property-picker-dropdown="pickerType"
            @click.stop
        >
            <div class="property-picker-search-container">
                <input
                    ref="searchInput"
                    type="search"
                    class="form-control form-control-sm property-picker-search"
                    :data-property-picker-search="pickerType"
                    placeholder="Поиск..."
                    autocomplete="off"
                    v-model="searchQuery"
                    @keydown.esc.prevent="closeDropdown"
                    @keydown.enter.prevent.stop="selectHighlighted"
                    @keydown.down.prevent="highlightNext"
                    @keydown.up.prevent="highlightPrev"
                >
            </div>
            <div ref="optionsList" class="property-picker-options-list">
                <!-- Fallback item (e.g. "Основной объект поступления" or "Объект поступления позиции") -->
                <button
                    v-if="showPlaceholderInList"
                    type="button"
                    class="dropdown-item property-picker-option"
                    :class="{
                        'active': !internalValue,
                        'is-highlighted': highlightedIndex === -1
                    }"
                    :title="placeholder"
                    data-property-picker-option=""
                    @mousedown.prevent="selectOption('')"
                >
                    {{ placeholder }}
                </button>

                <!-- Property items in exact hierarchy order -->
                <button
                    v-for="(opt, idx) in filteredOptions"
                    :key="opt.id"
                    type="button"
                    class="dropdown-item property-picker-option"
                    :class="{
                        'active': String(internalValue) === String(opt.id),
                        'is-highlighted': highlightedIndex === idx
                    }"
                    :title="opt.name"
                    :data-property-picker-option="opt.id"
                    @mousedown.prevent="selectOption(opt.id)"
                >
                    {{ opt.name }}
                </button>

                <!-- Empty search state -->
                <div v-if="!showPlaceholderInList && filteredOptions.length === 0" class="text-muted text-center py-2 small property-picker-empty">
                    Ничего не найдено
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PropertyPicker',
    props: {
        value: {
            type: [String, Number],
            default: ''
        },
        name: {
            type: String,
            default: ''
        },
        options: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: 'Основной объект поступления'
        },
        pickerType: {
            type: String,
            default: 'property'
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpen: false,
            searchQuery: '',
            internalValue: this.value !== null && this.value !== undefined ? String(this.value) : '',
            highlightedIndex: -1
        };
    },
    computed: {
        normalizedOptions() {
            if (!Array.isArray(this.options)) {
                return [];
            }
            return this.options.map(opt => ({
                id: String(opt.id !== undefined ? opt.id : opt.value),
                name: String(opt.name !== undefined ? opt.name : opt.label)
            }));
        },
        selectedOption() {
            if (!this.internalValue) {
                return null;
            }
            return this.normalizedOptions.find(opt => String(opt.id) === String(this.internalValue)) || null;
        },
        selectedLabel() {
            if (this.selectedOption) {
                return this.selectedOption.name;
            }
            return this.placeholder;
        },
        cleanSearchQuery() {
            return (this.searchQuery || '').trim().toLocaleLowerCase('ru-RU');
        },
        showPlaceholderInList() {
            if (!this.cleanSearchQuery) {
                return true;
            }
            return this.placeholder.toLocaleLowerCase('ru-RU').includes(this.cleanSearchQuery);
        },
        filteredOptions() {
            if (!this.cleanSearchQuery) {
                return this.normalizedOptions;
            }
            const q = this.cleanSearchQuery;
            return this.normalizedOptions.filter(opt =>
                opt.name.toLocaleLowerCase('ru-RU').includes(q)
            );
        }
    },
    watch: {
        value(newVal) {
            this.internalValue = newVal !== null && newVal !== undefined ? String(newVal) : '';
        },
        filteredOptions() {
            if (this.isOpen) {
                this.highlightedIndex = this.showPlaceholderInList ? -1 : (this.filteredOptions.length ? 0 : -1);
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) {
                return;
            }
            if (this.isOpen) {
                this.closeDropdown();
            } else {
                this.openDropdown();
            }
        },
        openDropdown() {
            if (this.disabled) {
                return;
            }
            this.isOpen = true;
            this.searchQuery = '';
            this.highlightedIndex = this.showPlaceholderInList ? -1 : (this.filteredOptions.length ? 0 : -1);
            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus();
                }
            });
        },
        closeDropdown() {
            this.isOpen = false;
            this.searchQuery = '';
            this.highlightedIndex = -1;
        },
        selectOption(id) {
            const val = id !== null && id !== undefined ? String(id) : '';
            this.internalValue = val;
            this.$emit('input', val);
            this.$emit('change', val);
            this.closeDropdown();
            this.$nextTick(() => {
                if (this.$refs.toggleBtn) {
                    this.$refs.toggleBtn.focus();
                }
            });
        },
        onNativeSelectChange(val) {
            const newVal = val !== null && val !== undefined ? String(val) : '';
            this.internalValue = newVal;
            this.$emit('input', newVal);
            this.$emit('change', newVal);
        },
        highlightNext() {
            const maxIndex = this.filteredOptions.length - 1;
            if (this.highlightedIndex < maxIndex) {
                this.highlightedIndex++;
                this.scrollToHighlighted();
            }
        },
        highlightPrev() {
            const minIndex = this.showPlaceholderInList ? -1 : 0;
            if (this.highlightedIndex > minIndex) {
                this.highlightedIndex--;
                this.scrollToHighlighted();
            }
        },
        selectHighlighted() {
            if (this.highlightedIndex === -1 && this.showPlaceholderInList) {
                this.selectOption('');
            } else if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
                this.selectOption(this.filteredOptions[this.highlightedIndex].id);
            } else if (this.showPlaceholderInList) {
                this.selectOption('');
            } else if (this.filteredOptions.length > 0) {
                this.selectOption(this.filteredOptions[0].id);
            }
        },
        scrollToHighlighted() {
            this.$nextTick(() => {
                const list = this.$refs.optionsList;
                if (!list) {
                    return;
                }
                const highlightedEl = list.querySelector('.is-highlighted');
                if (highlightedEl) {
                    highlightedEl.scrollIntoView({ block: 'nearest' });
                }
            });
        },
        handleDocumentClick(e) {
            if (this.isOpen && !this.$el.contains(e.target)) {
                this.closeDropdown();
            }
        }
    }
};
</script>

<style scoped>
.property-picker-wrapper {
    position: relative;
    width: 100%;
}

.property-picker-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 34px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    text-align: left;
    outline: none;
    font-size: 12px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.property-picker-toggle:hover,
.property-picker-toggle:focus {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.5);
    outline: none;
}

.property-picker-toggle.is-placeholder {
    color: #fff;
}

.property-picker-selected-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
}

.property-picker-toggle .caret {
    display: inline-block;
    margin-left: 8px;
    vertical-align: middle;
    border-top: 4px dashed rgba(255, 255, 255, 0.5);
    border-right: 4px solid transparent;
    border-left: 4px solid transparent;
    flex: 0 0 auto;
}

.property-picker-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1050;
    display: block;
    margin-top: 2px;
    padding: 4px 0;
    background-color: #fff;
    border: 1px solid #d9e0e7;
    border-radius: 4px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
    min-width: 100%;
    width: max-content;
    max-width: min(480px, calc(100vw - 32px));
}

.property-picker-search-container {
    padding: 4px 8px 6px 8px;
    border-bottom: 1px solid #eee;
}

.property-picker-search-container input[type="search"] {
    color: #000 !important;
    background-color: #fff !important;
    border: 1px solid #ccd0d4 !important;
    border-radius: 3px;
    padding: 4px 8px;
    font-size: 12px;
    width: 100%;
}

.property-picker-search-container input[type="search"]:focus {
    border-color: #348fe2 !important;
    outline: 0;
}

.property-picker-options-list {
    max-height: 220px;
    overflow-y: auto;
    padding: 2px 0;
}

.property-picker-option {
    display: block;
    width: 100%;
    padding: 4px 10px;
    clear: both;
    font-weight: 400;
    color: #2d353c;
    text-align: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: transparent;
    border: 0;
    cursor: pointer;
    font-size: 12px;
    line-height: 1.42857143;
}

.property-picker-option:hover,
.property-picker-option:focus,
.property-picker-option.is-highlighted {
    color: #fff;
    text-decoration: none;
    background-color: #348fe2;
    outline: none;
}

.property-picker-option.active {
    color: #fff;
    background-color: #2482e6;
    font-weight: 600;
}
</style>
